import { text } from '../../app/i18n';
import { Icon } from '../../design-system/icons';
import { blockMediaRef, type AggregateValue, type LayoutBlock } from '@ayumia/component-runtime';
import { aggregateShown } from '../FormLayoutParts';
import { useFormMedia } from './FormMedia';
import type { MediaRef } from './presentation';

/**
 * Blocs d'affichage et d'action des formulaires (ADR-146 addendum (20)) : Image (`FormImage`), Bouton
 * d'action (`ActionButton`), Puce de compteur (`CounterChip`). Même JSON qu'à la tablette. Un bloc
 * n'écrit jamais une réponse ; le bouton n'appelle que les gestes du formulaire (Suivant, Précédent,
 * Valider, ajout d'une ligne, ouverture d'une page) : la commande et le serveur décident toujours.
 */

type Localized = Readonly<Record<string, string>>;

const propsOf = (block: LayoutBlock): Readonly<Record<string, unknown>> => (block.props ?? {}) as Readonly<Record<string, unknown>>;
const localized = (value: unknown): string => (value !== null && typeof value === 'object' && !Array.isArray(value) ? text(value as Localized) : '');

/** Image de la médiathèque : une référence introuvable n'affiche rien (repli : texte de remplacement), jamais une erreur. */
export function FormImageBlock({ block }: { block: LayoutBlock }) {
  const media = useFormMedia();
  const props = propsOf(block);
  const ref = blockMediaRef(props['image']) as MediaRef | undefined;
  const url = media.resolve(ref);
  const alt = localized(props['alt']) || text(block.label as Localized | undefined);
  const variant = block.variant === 'cover' || block.variant === 'banner' ? block.variant : 'contain';
  const size = block.size ?? 'normal';
  return (
    <figure className={`ay-fv2-image ay-fv2-image--${variant} ay-fv2-image--${size}`} data-testid={`block-${block.block_id}`}>
      {url !== undefined ? <img src={url} alt={alt} /> : alt !== '' ? <figcaption className="ay-fv2-image__fallback">{alt}</figcaption> : null}
    </figure>
  );
}

/** Gestes du formulaire qu'un bouton d'action peut déclencher. */
export interface FormButtonGestures {
  next?: (() => void) | undefined;
  previous?: (() => void) | undefined;
  submit?: (() => void) | undefined;
  addRow?: ((section: string) => void) | undefined;
  openPage?: ((page: string) => void) | undefined;
}

const ACTION_LABEL: Readonly<Record<string, string>> = { next_page: 'Suivant', previous_page: 'Précédent', submit: 'Valider la fiche', add_row: 'Ajouter une ligne', open_page: 'Ouvrir la page' };

/** Bouton d'action : une action de la liste fermée ; une action sans geste sur cette page est grisée. */
export function ActionButtonBlock({ block, gestures, disabled }: { block: LayoutBlock; gestures: FormButtonGestures; disabled: boolean }) {
  const props = propsOf(block);
  const action = typeof props['action'] === 'string' ? props['action'] : '';
  const section = typeof props['section'] === 'string' ? props['section'] : undefined;
  const page = typeof props['page'] === 'string' ? props['page'] : undefined;
  const run: (() => void) | undefined =
    action === 'next_page'
      ? gestures.next
      : action === 'previous_page'
        ? gestures.previous
        : action === 'submit'
          ? gestures.submit
          : action === 'add_row' && section !== undefined && gestures.addRow !== undefined
            ? () => gestures.addRow!(section)
            : action === 'open_page' && page !== undefined && gestures.openPage !== undefined
              ? () => gestures.openPage!(page)
              : undefined;
  const label = localized(props['label']) || text(block.label as Localized | undefined) || ACTION_LABEL[action] || 'Action';
  const variant = block.variant === 'primary' ? 'filled' : block.variant === 'link' ? 'link' : 'outline';
  const icon = typeof props['icon'] === 'string' ? props['icon'] : undefined;
  return (
    <button type="button" className={`ay-fv2-button ay-fv2-button--${variant}`} data-testid={`block-${block.block_id}`} data-action={action} disabled={disabled || run === undefined} onClick={run}>
      {icon !== undefined && <Icon name={icon} size={18} />}
      {label}
    </button>
  );
}

/** Puce de compteur : la valeur exacte d'un total déclaré et son unité, comme les puces de la liste. */
export function CounterChipBlock({ block, aggregates }: { block: LayoutBlock; aggregates: readonly AggregateValue[] }) {
  const props = propsOf(block);
  const path = (block.bind as Readonly<Record<string, { path: string }>> | undefined)?.['value']?.path;
  const id = path?.startsWith('$aggregates.') === true ? path.slice('$aggregates.'.length) : undefined;
  const value = aggregates.find((candidate) => candidate.aggregate_id === id);
  const label = localized(props['label']) || (value === undefined ? '' : text(value.label));
  const icon = typeof props['icon'] === 'string' ? props['icon'] : undefined;
  const shown = value === undefined ? '—' : value.text === '' ? (value.unit === undefined ? '0' : `0 ${value.unit}`) : aggregateShown(value);
  return (
    <span className={`ay-fv2-chip${block.variant === 'accent' ? ' ay-fv2-chip--accent' : ''}`} title={label === '' ? undefined : label} data-testid={`block-${block.block_id}`} role="status" aria-label={label === '' ? shown : `${label} : ${shown}`}>
      {icon !== undefined && <Icon name={icon} size={15} />}
      {shown}
    </span>
  );
}
